<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useRadioLogStore } from '@/stores/radioLog'
import { MessageType } from '@/types'
import type { Timestamp } from 'firebase/firestore'

const route = useRoute()
const router = useRouter()
const incidentId = route.params.id as string

const radioLogStore = useRadioLogStore()
const recentLog = computed(() => radioLogStore.entries)

function logDotClass(messageType: string): string {
  if (messageType === MessageType.EmergencyTraffic) return 'bg-red-500'
  if (messageType === MessageType.PriorityTraffic) return 'bg-amber-400'
  if (messageType === MessageType.Clue) return 'bg-primary'
  return 'bg-outline-variant'
}

function formatLogTime(ts: Timestamp): string {
  const d = ts.toDate()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function go() {
  router.push({ name: 'incident-comms', params: { id: incidentId } })
}
</script>

<template>
  <div class="bg-surface border border-border rounded-lg p-4 flex flex-col h-[280px]">
    <div class="flex items-center justify-between mb-3 shrink-0">
      <span class="text-[10px] font-label uppercase tracking-widest text-outline">Recent radio log</span>
      <button
        class="p-0 text-[11px] text-primary font-label uppercase tracking-wide hover:underline"
        @click="go"
      >
        View all →
      </button>
    </div>

    <div v-if="recentLog.length" class="flex flex-col divide-y divide-border overflow-y-auto flex-1 min-h-0">
      <div
        v-for="entry in recentLog"
        :key="entry.entry_id"
        class="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0"
      >
        <span class="text-[10px] font-mono text-outline shrink-0 mt-0.5 min-w-[36px]">
          {{ formatLogTime(entry.client_timestamp) }}
        </span>
        <span :class="['w-1.5 h-1.5 rounded-full shrink-0 mt-1.5', logDotClass(entry.message_type)]" />
        <div class="flex-1 min-w-0 flex items-baseline gap-2 overflow-hidden">
          <span class="text-[12px] font-medium text-on-surface shrink-0">{{ entry.team_identifier }}</span>
          <span class="text-[11px] text-outline shrink-0">{{ entry.message_type }}</span>
          <span
            v-if="(entry.message_type === MessageType.StartingAssignment || entry.message_type === MessageType.CompletedAssignment) && entry.assignment_name"
            class="text-[11px] font-medium text-on-surface truncate"
          >{{ entry.assignment_name }}</span>
          <span v-else-if="entry.notes" class="text-[11px] text-on-surface-variant truncate">{{ entry.notes }}</span>
        </div>
      </div>
    </div>

    <p v-else class="text-sm text-outline italic">No radio log entries yet.</p>
  </div>
</template>
